import { z } from 'zod'
import type { ClaimCalcResult, ClaimTerms, ClaimToDate, RecoveryMethod } from './claim-calc.js'
import { moneySchema, optionalText, quantitySchema, versionSchema } from './common.js'
import type { ProjectUserRef } from './projects.js'
import type { BillingDocumentRef, TaxRateRefLite } from './projects-billing.js'
import type { CostCategoryRef, WbsNodeRef } from './projects-costs.js'

/**
 * Projects release P4, contracting (docs/refactor/projects/04-contracting.md, P4_BREAKDOWN.md, ADR-040):
 * the contract and its terms, the bill of quantities, variations, progress claims certified into Sales
 * invoices (GL role `contract_revenue`) with a Projects journal for retention and advance recovery, the
 * advance invoice (`customer_advances`) and retention releases (`retention_receivable`). Arithmetic is
 * in `claim-calc.ts`. Amounts need projects.contract or projects.financials to read.
 */

const requiredText = (max: number, message = 'Required') => z.string().trim().min(1, message).max(max)
const nullableUuid = z
  .uuid()
  .nullish()
  .transform((v) => v ?? null)
const nullableDate = z.iso
  .date()
  .nullish()
  .transform((v) => v ?? null)
/** 0–100 with up to 4 decimals (retention rates, recovery rates, cumulative claim percentages). */
const pct4 = z
  .string()
  .regex(/^\d{1,3}(\.\d{1,4})?$/, 'Invalid percentage')
  .refine((v) => Number(v) <= 100, 'At most 100')
const nonNegativeMoney = moneySchema.refine((v) => Number(v) >= 0, 'Cannot be negative')
const positiveQty = quantitySchema.refine((v) => Number(v) > 0, 'Quantity must be above zero')
const reason = requiredText(1000, 'Give a reason')

// ── settings (group `projects`, key `contracting`) ───────────────────────────

export const MEASURE_BY = ['QUANTITY', 'PERCENT'] as const
export type MeasureBy = (typeof MEASURE_BY)[number]
export const RETENTION_STAGES = ['COMPLETION', 'DLP_END', 'OTHER'] as const
export type RetentionStage = (typeof RETENTION_STAGES)[number]

const releaseStagesSchema = z
  .array(z.object({ stage: z.enum(RETENTION_STAGES), percent: pct4 }))
  .min(1)
  .max(4)
  .refine((stages) => Math.abs(stages.reduce((s, x) => s + Number(x.percent), 0) - 100) < 1e-9, {
    message: 'The stages must add up to 100%',
  })

/**
 * Contracting defaults (04 §3.2). Copied into a contract while it is DRAFT; an ACTIVE contract changes
 * only by "Amend terms". Oman figures are UNVERIFIED assumptions (ASM-200 to ASM-208).
 */
export const contractingSettingsSchema = z.object({
  measureDefault: z.enum(MEASURE_BY).default('QUANTITY'),
  /** Measuring above an item's current quantity: warn, or refuse (OVER_MEASURE). */
  overMeasure: z.enum(['WARN', 'BLOCK']).default('WARN'),
  paymentDays: z.number().int().min(0).max(365).default(30),
  retentionRatePct: pct4.default('10'),
  /** null: no cap. */
  retentionCapPct: pct4.nullable().default('5'),
  retentionCapBasis: z.enum(['ORIGINAL', 'CURRENT']).default('ORIGINAL'),
  releaseStages: releaseStagesSchema.default([
    { stage: 'COMPLETION', percent: '50' },
    { stage: 'DLP_END', percent: '50' },
  ]),
  dlpMonths: z.number().int().min(0).max(60).default(12),
  /** AT_RELEASE only in P4 (ASM-201); AT_CLAIM may come later (04 §15.4). */
  retentionVatTiming: z.enum(['AT_RELEASE']).default('AT_RELEASE'),
  advancePercent: pct4.default('10'),
  recovery: z.enum(['PRO_RATA', 'FIXED_RATE', 'MANUAL']).default('PRO_RATA'),
  fixedRatePct: pct4.default('25'),
  startThresholdPct: pct4.default('10'),
  /** FIDIC 1999 cl. 14.2 measures the threshold without provisional sums (04 open point 10). */
  thresholdBasis: z.enum(['ORIGINAL', 'ORIGINAL_LESS_PROVISIONAL']).default('ORIGINAL'),
  /** Optional: used only to propose liquidated damages (ASM-203: they reduce the value before VAT). */
  ldPerDay: nonNegativeMoney.nullable().default(null),
  ldCapPct: pct4.nullable().default(null),
  /**
   * Internal approval before a variation or a claim goes to the client (P5): NONE submits at once;
   * WORKFLOW routes the submit through an approval workflow (subjects `project_variation`,
   * `project_claim`), waiting in PENDING_APPROVAL. With Workflows off or no workflow applying, a second
   * person holding projects.contract_approve approves (four eyes).
   */
  variationsApproval: z.enum(['NONE', 'WORKFLOW']).default('NONE'),
  claimsApproval: z.enum(['NONE', 'WORKFLOW']).default('NONE'),
  /** Guarantee reminders, days before expiry (P5), and the usual performance bond %. */
  guaranteeReminderDays: z.array(z.number().int().min(1).max(365)).max(5).default([30, 14, 7]),
  performanceBondPct: pct4.default('5'),
})
export type ContractingSettings = z.output<typeof contractingSettingsSchema>
export const DEFAULT_CONTRACTING_SETTINGS: ContractingSettings = contractingSettingsSchema.parse({})

// ── contract and terms ───────────────────────────────────────────────────────

/** DRAFT → ACTIVE (activate) → COMPLETED (practical completion) → CLOSED (final account, P5). */
export const CONTRACT_STATUSES = ['DRAFT', 'ACTIVE', 'COMPLETED', 'CLOSED'] as const
export type ContractStatus = (typeof CONTRACT_STATUSES)[number]

const termsFields = {
  contractNumber: optionalText(60),
  clientReference: optionalText(100),
  /** The contract's VAT rate (one per contract, ASM-206); fixed once a claim is certified. */
  taxRateId: z.uuid(),
  commencementDate: nullableDate,
  completionDue: nullableDate,
  paymentDays: z.number().int().min(0).max(365),
  measureDefault: z.enum(MEASURE_BY),
  overMeasure: z.enum(['WARN', 'BLOCK']),
  retentionRatePct: pct4,
  retentionCapPct: pct4.nullable(),
  retentionCapBasis: z.enum(['ORIGINAL', 'CURRENT']),
  releaseStages: releaseStagesSchema,
  dlpMonths: z.number().int().min(0).max(60),
  advancePercent: pct4,
  recovery: z.enum(['PRO_RATA', 'FIXED_RATE', 'MANUAL']),
  fixedRatePct: pct4,
  startThresholdPct: pct4,
  thresholdBasis: z.enum(['ORIGINAL', 'ORIGINAL_LESS_PROVISIONAL']),
  ldPerDay: nonNegativeMoney.nullable(),
  ldCapPct: pct4.nullable(),
}
export const contractTermsSchema = z.object(termsFields)
export type ContractTerms = z.output<typeof contractTermsSchema>

/** `PUT /projects/:id/contract`: a DRAFT contract's terms (created on first save, from the settings). */
export const contractDraftSchema = contractTermsSchema.extend({ version: versionSchema.optional() })
export type ContractDraftInput = z.input<typeof contractDraftSchema>

/**
 * `POST /projects/:id/contract/activate`: the chosen DRAFT BOQ version becomes CONTRACT, other drafts
 * SUPERSEDED, the original sum = its total (refusals BOQ_REQUIRED, CUSTOMER_REQUIRED, CONTRACT_ACTIVE).
 */
export const contractActivateSchema = z.object({ version: versionSchema, boqVersionId: z.uuid() })
/**
 * `POST /projects/:id/contract/amend`: changed terms of an ACTIVE contract with a reason. Not the tax
 * rate once a claim is certified; never the original sum (variations change the contract value).
 */
export const contractAmendSchema = z.object({
  version: versionSchema,
  reason,
  terms: contractTermsSchema.partial(),
})
export type ContractAmendInput = z.input<typeof contractAmendSchema>
/** `POST /projects/:id/contract/practical-completion`: COMPLETED, DLP end = date + dlpMonths. */
export const practicalCompletionSchema = z.object({ version: versionSchema, date: z.iso.date() })

/** The contract's money position (04 §6 `ContractPositionService`), all derived. */
export interface ContractPosition {
  originalSum: string
  approvedVariations: string
  pendingVariations: string
  /** original + approved variations. */
  currentValue: string
  certifiedGross: string
  penalties: string
  retentionHeld: string
  retentionReleased: string
  retentionBalance: string
  advanceInvoiced: string
  advanceRecovered: string
  advanceOutstanding: string
  /**
   * P5: retention forfeited (ACTIVE forfeits) and the advance reduced (ACTIVE reduction notes, net of VAT);
   * `retentionBalance` = held − released − forfeited, `advanceOutstanding` = invoiced − reduced − recovered.
   * Optional only for older fixtures.
   */
  retentionForfeited?: string
  advanceReduced?: string
  /** VAT-inclusive totals of the contract's ACTIVE Sales documents (claims, advance, releases). */
  invoiced: string
  received: string
  outstanding: string
}

export interface ContractAmendment {
  at: string
  by: ProjectUserRef | null
  reason: string
  /** Changed terms: old and new values as shown. */
  changes: { key: string; from: string | null; to: string | null }[]
}

export type ContractCommand = 'edit' | 'activate' | 'amend' | 'practicalCompletion'

export interface ProjectContract {
  id: string
  status: ContractStatus
  terms: ContractTerms
  taxRate: TaxRateRefLite
  /** The CONTRACT BOQ version once active. */
  boqVersion: { id: string; name: string; total: string } | null
  originalSum: string | null
  activatedAt: string | null
  practicalCompletion: string | null
  /** Actual (after practical completion) or planned (completion due + DLP). */
  dlpEnd: string | null
  dlpEndPlanned: boolean
  position: ContractPosition | null
  amendments: ContractAmendment[]
  can: Record<ContractCommand, boolean>
  version: number
}

// ── bill of quantities ───────────────────────────────────────────────────────

export const BOQ_VERSION_STATUSES = ['DRAFT', 'CONTRACT', 'SUPERSEDED'] as const
export type BoqVersionStatus = (typeof BOQ_VERSION_STATUSES)[number]
export const BOQ_BASES = ['REMEASURE', 'LUMP_SUM'] as const
export type BoqBasis = (typeof BOQ_BASES)[number]
export const BOQ_ITEM_KINDS = [
  'MEASURED',
  'LUMP_SUM',
  'PROVISIONAL_SUM',
  'PRIME_COST',
  'DAYWORK',
  'CONTINGENCY',
] as const
export type BoqItemKind = (typeof BOQ_ITEM_KINDS)[number]
/** Counted in the contract sum and claimed by percentage or replaced by a PS_EXPENDITURE variation. */
export const BOQ_ALLOWANCE_KINDS: readonly BoqItemKind[] = ['PROVISIONAL_SUM', 'PRIME_COST', 'CONTINGENCY']

/** `POST /projects/:id/boq/versions`: a DRAFT; empty, or a copy of another version. */
export const boqVersionCreateSchema = z.object({
  name: requiredText(120),
  basis: z.enum(BOQ_BASES).default('REMEASURE'),
  copyFromVersionId: nullableUuid,
})
export type BoqVersionCreateInput = z.input<typeof boqVersionCreateSchema>
export const boqVersionUpdateSchema = z.object({
  name: requiredText(120),
  basis: z.enum(BOQ_BASES),
  version: versionSchema,
})

export const boqSectionInputSchema = z.object({
  parentId: nullableUuid,
  code: requiredText(40),
  title: requiredText(300),
  titleAr: optionalText(300),
})
export type BoqSectionInput = z.input<typeof boqSectionInputSchema>
export const boqSectionUpdateSchema = boqSectionInputSchema.extend({ version: versionSchema })

/** amount = round3(quantity × rate) (04 §8.2 rule 1); quantity > 0, rate ≥ 0 (zero: "included"). */
const boqItemFields = z.object({
  sectionId: nullableUuid,
  itemNo: requiredText(40),
  description: requiredText(2000),
  descriptionAr: optionalText(2000),
  unit: requiredText(20),
  quantity: positiveQty,
  rate: nonNegativeMoney,
  kind: z.enum(BOQ_ITEM_KINDS).default('MEASURED'),
  /** Default: the contract's `measureDefault`; PERCENT on a LUMP_SUM version. */
  measureBy: z.enum(MEASURE_BY).optional(),
  wbsNodeId: nullableUuid,
  costCategoryId: nullableUuid,
  /** Expected cost per unit (budget from BOQ, margin). */
  costRate: nonNegativeMoney.nullish().transform((v) => v ?? null),
})
export const boqItemInputSchema = boqItemFields
export type BoqItemInput = z.input<typeof boqItemInputSchema>
export const boqItemUpdateSchema = boqItemFields.extend({ version: versionSchema })

export interface BoqVersion {
  id: string
  name: string
  status: BoqVersionStatus
  basis: BoqBasis
  /** Σ rounded item amounts. */
  total: string
  itemCount: number
  /** Where it came from: typed, a CSV import, a quotation, a copy. */
  source: 'MANUAL' | 'IMPORT' | 'QUOTATION' | 'COPY'
  createdAt: string
  activatedAt: string | null
  version: number
}

export interface BoqSection {
  id: string
  parentId: string | null
  code: string
  title: string
  titleAr: string | null
  position: number
  /** Σ amounts of its items and sub-sections. */
  total: string
  version: number
}

export interface BoqItem {
  id: string
  sectionId: string | null
  itemNo: string
  description: string
  descriptionAr: string | null
  unit: string
  quantity: string
  rate: string
  amount: string
  kind: BoqItemKind
  measureBy: MeasureBy
  wbsNode: WbsNodeRef | null
  costCategory: CostCategoryRef | null
  costRate: string | null
  /** amount − round3(quantity × costRate); null without a cost rate. */
  margin: string | null
  version: number
}

export interface BoqTree {
  version: BoqVersion
  sections: BoqSection[]
  items: BoqItem[]
}

/** The CSV columns of import and export (04 §8.2 rule 8, ASM-213). */
export const BOQ_CSV_COLUMNS = [
  'section_path',
  'item_no',
  'description',
  'description_ar',
  'unit',
  'quantity',
  'rate',
  'amount',
  'kind',
  'measure_by',
  'wbs_code',
  'cost_category',
  'cost_rate',
] as const

/** `POST …/boq/versions/:vid/import/preview`: the CSV uploaded through the files module. */
export const boqImportPreviewSchema = z.object({ fileId: z.uuid() })
/** `POST …/import/apply`: re-plans in one transaction; 409 IMPORT_STALE when the plan changed. */
export const boqImportApplySchema = z.object({ fileId: z.uuid(), fingerprint: z.string().min(16).max(128) })

export interface BoqImportPlan {
  fingerprint: string
  counts: { create: number; update: number; unchanged: number; sections: number; errors: number }
  /** Row errors (row number as in the file, header = 1). A plan with errors can't be applied. */
  errors: { row: number; column: string | null; message: string }[]
  totalBefore: string
  totalAfter: string
}

/**
 * `GET /projects/:id/boq/current`: what a claim measures (04 §6): CONTRACT items at their current
 * quantity (+ approved CHANGE_QTY / OMIT at the item's rate) and every approved line with its own rate
 * (ADD, star rates), with what is certified to date.
 */
export interface CurrentBoqLine {
  /** `item:<id>` or `vline:<id>`. */
  key: string
  source: 'ITEM' | 'VARIATION'
  /**
   * The bill › section codes the line sits under (for section header rows), in order; empty for lines on
   * no section. Lines arrive sorted by section path, then position. Optional only for older fixtures.
   */
  sectionPath?: { id: string; code: string; title: string; titleAr: string | null }[]
  itemNo: string
  description: string
  descriptionAr: string | null
  unit: string
  /** Current quantity (contract ± approved variations). */
  quantity: string
  rate: string
  /** round3(quantity × rate). */
  amount: string
  kind: BoqItemKind
  measureBy: MeasureBy
  variation: { id: string; number: string } | null
  certifiedQuantity: string | null
  certifiedPercent: string | null
  certifiedValue: string
}

/** `POST …/boq/versions/:vid/budget` (projects.budgets): proposes a budget revision (02) from cost rates. */
export interface BoqBudgetProposal {
  budgetId: string
  versionNo: number
  totalCost: string
  /** Items left out: no cost category or no cost rate. */
  skipped: { itemNo: string; reason: 'NO_CATEGORY' | 'NO_COST_RATE' }[]
}

// ── variations ───────────────────────────────────────────────────────────────

export const VARIATION_KINDS = [
  'CLIENT_INSTRUCTION',
  'DESIGN_CHANGE',
  'SITE_CONDITION',
  'PS_EXPENDITURE',
  'OMISSION',
] as const
export type VariationKind = (typeof VARIATION_KINDS)[number]
/** PENDING_APPROVAL arrives with internal approval workflows (P5). */
export const VARIATION_STATUSES = [
  'DRAFT',
  'PENDING_APPROVAL',
  'SUBMITTED',
  'APPROVED',
  'REJECTED',
  'WITHDRAWN',
] as const
export type VariationStatus = (typeof VARIATION_STATUSES)[number]
export const VARIATION_LINE_ACTIONS = ['ADD', 'CHANGE_QTY', 'OMIT'] as const
export type VariationLineAction = (typeof VARIATION_LINE_ACTIONS)[number]

/**
 * ADD: a new item (`VO3.2`) with its own unit, quantity and rate. CHANGE_QTY: ± quantity on an item at its
 * rate, or at a star rate (`starRate`, then its own claimable line). OMIT: a negative quantity on an item
 * at its rate. amount = round3(quantity × rate), may be negative.
 */
export const variationLineInputSchema = z.object({
  action: z.enum(VARIATION_LINE_ACTIONS),
  /** CHANGE_QTY and OMIT: the current-BOQ line it changes (`item:<id>` or `vline:<id>`). */
  targetKey: z
    .string()
    .max(80)
    .nullish()
    .transform((v) => v ?? null),
  /** ADD (and star-rate CHANGE_QTY): the new item number, e.g. VO3.2. */
  itemNo: optionalText(40),
  description: optionalText(2000),
  unit: optionalText(20),
  /** Signed: OMIT and reductions negative. */
  quantity: quantitySchema.refine((v) => Number(v) !== 0, 'Enter a quantity'),
  /** ADD and star rates: the rate; null: the target item's rate. */
  rate: nonNegativeMoney.nullish().transform((v) => v ?? null),
  wbsNodeId: nullableUuid,
  costCategoryId: nullableUuid,
  costRate: nonNegativeMoney.nullish().transform((v) => v ?? null),
})
export type VariationLineInput = z.input<typeof variationLineInputSchema>

const variationFields = z.object({
  kind: z.enum(VARIATION_KINDS),
  title: requiredText(300),
  description: optionalText(4000),
  /** The client's instruction reference and date (site instruction, engineer's letter). */
  instructionRef: optionalText(100),
  instructionDate: nullableDate,
  /** Working days added to completion (and the planned DLP end) when approved. */
  timeImpactDays: z.number().int().min(0).max(2000).default(0),
  /** Expected cost of the change (budget revision when lines carry no cost rate). */
  costEstimate: nonNegativeMoney.nullish().transform((v) => v ?? null),
  lines: z.array(variationLineInputSchema).max(500),
})
/** `POST /projects/:id/variations`: numbered VO-NNN at creation (series per project). */
export const variationInputSchema = variationFields
export type VariationInput = z.input<typeof variationInputSchema>
/** `PATCH /projects/:id/variations/:vid`: DRAFT only (VARIATION_FROZEN). */
export const variationUpdateSchema = variationFields.extend({ version: versionSchema })
export const variationCommandSchema = z.object({ version: versionSchema })
export const variationWithdrawSchema = z.object({ version: versionSchema, reason })
/** `POST …/approve` (projects.variations_approve): the client's decision, per line approved quantity and rate. */
export const variationApproveSchema = z.object({
  version: versionSchema,
  clientRef: requiredText(100),
  decidedOn: z.iso.date(),
  lines: z
    .array(
      z.object({
        lineId: z.uuid(),
        approvedQuantity: quantitySchema,
        approvedRate: nonNegativeMoney,
      }),
    )
    .max(500)
    .default([]),
})
export type VariationApproveInput = z.input<typeof variationApproveSchema>
export const variationRejectSchema = z.object({
  version: versionSchema,
  reason,
  clientRef: optionalText(100),
})

export interface VariationLine {
  id: string
  action: VariationLineAction
  targetKey: string | null
  target: { itemNo: string; description: string } | null
  itemNo: string | null
  description: string
  unit: string
  quantity: string
  rate: string
  amount: string
  starRate: boolean
  approvedQuantity: string | null
  approvedRate: string | null
  approvedAmount: string | null
  wbsNode: WbsNodeRef | null
  costCategory: CostCategoryRef | null
  costRate: string | null
}

export type VariationCommand = 'edit' | 'submit' | 'withdraw' | 'approve' | 'reject'

export interface Variation {
  id: string
  number: string
  kind: VariationKind
  title: string
  description: string | null
  status: VariationStatus
  instructionRef: string | null
  instructionDate: string | null
  timeImpactDays: number
  costEstimate: string | null
  /** Σ line amounts as submitted (draft: as now). */
  submittedValue: string
  approvedValue: string | null
  clientRef: string | null
  decidedOn: string | null
  decisionReason: string | null
  submittedAt: string | null
  lines: VariationLine[]
  can: Record<VariationCommand, boolean>
  /** P5: the internal approval before it goes to the client. Optional only for older fixtures. */
  internalApproval?: InternalApproval
  version: number
}

export type VariationListItem = Omit<Variation, 'lines' | 'description' | 'can' | 'internalApproval'> & {
  lineCount: number
}

// ── claims ───────────────────────────────────────────────────────────────────

export const CLAIM_KINDS = ['INTERIM', 'FINAL'] as const
export type ClaimKind = (typeof CLAIM_KINDS)[number]
/** PENDING_APPROVAL arrives with internal approval workflows (P5). */
export const CLAIM_STATUSES = [
  'DRAFT',
  'PENDING_APPROVAL',
  'SUBMITTED',
  'CERTIFIED',
  'REJECTED',
  'VOID',
] as const
export type ClaimStatus = (typeof CLAIM_STATUSES)[number]

/** `POST /projects/:id/claims`: one open claim per project (CLAIM_OPEN); none after a FINAL is certified. */
export const claimCreateSchema = z.object({
  kind: z.enum(CLAIM_KINDS).default('INTERIM'),
  periodTo: z.iso.date(),
})
export type ClaimCreateInput = z.input<typeof claimCreateSchema>

const cumLine = z.object({
  /** The current-BOQ line key. */
  key: z.string().min(1).max(80),
  cumQuantity: quantitySchema.nullish().transform((v) => v ?? null),
  /** Format only: the service answers PERCENT_OVER_100 above 100 (so the line gets its own message). */
  cumPercent: z
    .string()
    .regex(/^\d{1,3}(\.\d{1,4})?$/, 'Invalid percentage')
    .nullish()
    .transform((v) => v ?? null),
})

/** `PATCH /projects/:id/claims/:cid` on a DRAFT (projects.contract): the applied cumulative figures. */
export const claimAppliedUpdateSchema = z.object({
  version: versionSchema,
  kind: z.enum(CLAIM_KINDS).optional(),
  periodTo: z.iso.date().optional(),
  notes: optionalText(4000),
  lines: z.array(cumLine).max(5000),
  /** Liquidated damages to date (cumulative). */
  penaltyToDate: nonNegativeMoney.optional(),
  /** MANUAL recovery: to date. */
  manualRecoveryToDate: nonNegativeMoney.nullish().transform((v) => v ?? null),
})
export type ClaimAppliedUpdateInput = z.input<typeof claimAppliedUpdateSchema>

const override = z.object({ amount: nonNegativeMoney, reason })
/**
 * `PATCH /projects/:id/claims/:cid` on a SUBMITTED claim (projects.claims_certify): the client's certified
 * cumulative figures (default: as applied). A value or deduction may be set to the baisa with a reason
 * (OVERRIDE_REASON_REQUIRED).
 */
export const claimCertifiedUpdateSchema = z.object({
  version: versionSchema,
  lines: z
    .array(
      cumLine.extend({
        valueOverride: nonNegativeMoney.nullish().transform((v) => v ?? null),
        overrideReason: optionalText(1000),
      }),
    )
    .max(5000),
  penaltyToDate: nonNegativeMoney.optional(),
  retentionOverride: override.nullish().transform((v) => v ?? null),
  recoveryOverride: override.nullish().transform((v) => v ?? null),
})
export type ClaimCertifiedUpdateInput = z.input<typeof claimCertifiedUpdateSchema>

/** `POST …/submit` allocates IPC-NN and freezes the applied figures. */
export const claimCommandSchema = z.object({ version: versionSchema })
/**
 * `POST …/certify` (projects.claims_certify): one transaction creates the posted invoice for the net (GL
 * role contract_revenue), the Projects journal J1 and the link. A retry with the same request id
 * returns the first result.
 */
/** `POST …/certify/preview`: what certifying on this date would create (writes nothing). */
export const claimCertifyPreviewSchema = z.object({ certifiedOn: z.iso.date().optional() })
export type ClaimCertifyPreviewInput = z.input<typeof claimCertifyPreviewSchema>
export const claimCertifySchema = z.object({
  version: versionSchema,
  requestId: z.uuid(),
  certifiedOn: z.iso.date(),
  clientRef: requiredText(100),
})
export type ClaimCertifyInput = z.input<typeof claimCertifySchema>
export const claimRejectSchema = z.object({ version: versionSchema, reason, clientRef: optionalText(100) })
/** Only the latest certified claim (CLAIM_NOT_LATEST, CLAIM_HAS_LATER, RETENTION_RELEASED). */
export const claimVoidSchema = z.object({ version: versionSchema, reason })

export interface ClaimFigures {
  quantity: string | null
  percent: string | null
  value: string
}

export interface ClaimLine
  extends Omit<CurrentBoqLine, 'certifiedQuantity' | 'certifiedPercent' | 'certifiedValue'> {
  previous: ClaimFigures
  applied: ClaimFigures
  /** SUBMITTED and later: the client's figures. */
  certified: (ClaimFigures & { override: boolean; overrideReason: string | null }) | null
  /** Measuring issue of the applied figures (WARN shows; BLOCK refuses at save and submit). */
  issue: 'OVER_MEASURE' | 'PERCENT_OVER_100' | null
}

export type ClaimCommand = 'edit' | 'delete' | 'submit' | 'editCertified' | 'certify' | 'reject' | 'void'
export type ClaimBlockReason =
  | 'NOT_PERMITTED'
  | 'ILLEGAL_MOVE'
  | 'NEGATIVE_CERTIFICATE'
  | 'RETENTION_BELOW_RELEASED'
  | 'RECOVERY_ABOVE_ADVANCE'
  | 'RETENTION_ABOVE_GROSS'
  | 'CLAIM_NOT_LATEST'
  | 'CLAIM_HAS_LATER'
  | 'RETENTION_RELEASED'
  | 'INVOICE_HAS_PAYMENTS'
  | 'SALES_MODULE_OFF'
  | 'FEATURE_OFF'

export interface ClaimListItem {
  id: string
  /** IPC-NN from submission; null while DRAFT. */
  number: string | null
  kind: ClaimKind
  status: ClaimStatus
  periodTo: string
  submittedOn: string | null
  certifiedOn: string | null
  clientRef: string | null
  /** The figures that count: certified once certified, else applied. */
  grossToDate: string
  grossThisPeriod: string
  retention: string
  recovery: string
  penalty: string
  net: string
  vat: string
  total: string
  invoice: BillingDocumentRef | null
  version: number
}

export interface Claim extends ClaimListItem {
  notes: string | null
  rejectReason: string | null
  voidReason: string | null
  lines: ClaimLine[]
  /** The calculator's result on the applied figures. */
  applied: ClaimCalcResult
  /** On the certified figures (SUBMITTED and later). */
  certifiedResult: ClaimCalcResult | null
  retentionOverride: { amount: string; reason: string } | null
  recoveryOverride: { amount: string; reason: string } | null
  manualRecoveryToDate: string | null
  /**
   * The calculator's inputs as the server uses them (the contract's terms, the previous certificate's
   * to-date figures, retention released), so the editor's preview equals the server's figures exactly.
   */
  calc: { terms: ClaimTerms; previous: ClaimToDate; retentionReleased: string }
  /** The Projects journal of the certificate (J1). */
  journalEntryId: string | null
  can: Record<ClaimCommand, boolean>
  blocked: Partial<Record<ClaimCommand, ClaimBlockReason>>
  /** P5: the internal approval before it goes to the client. Optional only for older fixtures. */
  internalApproval?: InternalApproval
}

/**
 * `POST /projects/:id/claims/:cid/certify/preview`: what certifying would create, writing nothing: the
 * invoice (lines, VAT, total, due date) and the journal lines.
 */
export interface CertificatePreview {
  result: ClaimCalcResult
  invoice: {
    invoiceDate: string
    dueDate: string
    reference: string
    description: string
    net: string
    vat: string
    total: string
  }
  journal: { account: string; role: string; debit: string; credit: string }[]
}

// ── advance and retention ────────────────────────────────────────────────────

/** `POST /projects/:id/advances` (projects.bill): one per contract (ADVANCE_EXISTS); percent or amount. */
export const advanceIssueSchema = z
  .object({
    requestId: z.uuid(),
    percent: pct4.nullish().transform((v) => v ?? null),
    amount: moneySchema
      .refine((v) => Number(v) > 0, 'Enter an amount above zero')
      .nullish()
      .transform((v) => v ?? null),
    invoiceDate: z.iso.date(),
  })
  .refine((v) => (v.percent === null) !== (v.amount === null), {
    path: ['percent'],
    message: 'Give a percentage or an amount',
  })
export type AdvanceIssueInput = z.input<typeof advanceIssueSchema>
/** `POST /projects/:id/advances/:documentId/void`: only before any recovery is certified (ADVANCE_RECOVERED). */
export const advanceVoidSchema = z.object({ reason })

export interface AdvanceSummary {
  /** The advance invoice (ACTIVE), if issued. */
  invoice: BillingDocumentRef | null
  /** Net of VAT. */
  invoiced: string
  recovered: string
  outstanding: string
  /** Proposed amount: the contract's advance percent of the original sum. */
  proposed: string | null
  recoveries: { claimId: string; number: string; certifiedOn: string; amount: string }[]
  /**
   * P5: the advance reduced by Projects-issued credit notes (ACTIVE, net of VAT); `outstanding` = invoiced −
   * reduced − recovered, and the calculator's A = invoiced − reduced. Optional only for older fixtures.
   */
  reduced?: string
  reductions?: AdvanceReduction[]
  /**
   * The VAT percentage the advance invoice charged (its first line's stored rate, '0.00' without VAT): a
   * reduction credits VAT at it even after the rate's percentage was edited. Null before the advance
   * invoice. Optional only for older fixtures; without it the contract's rate is the best guess.
   */
  invoiceTaxRate?: string | null
  /** `reduce` (P5): projects.bill, an outstanding advance. Optional only for older fixtures. */
  can: { issue: boolean; void: boolean; reduce?: boolean }
}

/** `POST /projects/:id/retention/releases` (projects.bill): at most held − released (RETENTION_EXCEEDS_HELD). */
export const retentionReleaseSchema = z.object({
  requestId: z.uuid(),
  stage: z.enum(RETENTION_STAGES),
  amount: moneySchema.refine((v) => Number(v) > 0, 'Enter an amount above zero'),
  invoiceDate: z.iso.date(),
  /** Required before the stage is due. */
  reason: optionalText(1000),
})
export type RetentionReleaseInput = z.input<typeof retentionReleaseSchema>
export const retentionReleaseVoidSchema = z.object({ reason })

export interface RetentionRelease {
  id: string
  stage: RetentionStage
  amount: string
  invoiceDate: string
  status: 'ACTIVE' | 'VOID'
  reason: string | null
  invoice: BillingDocumentRef | null
  canVoid: boolean
}

export interface RetentionSummary {
  held: string
  released: string
  balance: string
  stages: {
    stage: RetentionStage
    percent: string
    /** When it falls due: practical completion, DLP end (actual or planned); null for OTHER. */
    dueOn: string | null
    due: boolean
    /** percent × held at the end (or so far), less what was released for it. */
    proposed: string
    released: string
  }[]
  byCertificate: { claimId: string; number: string; certifiedOn: string; period: string; toDate: string }[]
  releases: RetentionRelease[]
  /** P5: retention forfeited (ACTIVE); `balance` = held − released − forfeited. Optional for older fixtures. */
  forfeited?: string
  forfeits?: RetentionForfeit[]
  /** `forfeit` (P5): projects.bill, retention held. Optional only for older fixtures. */
  can: { release: boolean; forfeit?: boolean }
}

// ── reconciliation ───────────────────────────────────────────────────────────

/**
 * `GET /projects/contracting/reconciliation?asOf=` (projects.financials and ledger.read): the identities
 * of 04 §12 for the tenant; every difference should be zero.
 */
export const reconciliationQuerySchema = z.object({ asOf: z.iso.date().optional() })
export const RECONCILIATION_IDENTITIES = ['ADVANCES', 'RETENTION', 'REVENUE'] as const
export type ReconciliationIdentity = (typeof RECONCILIATION_IDENTITIES)[number]
export interface ReconciliationLine {
  identity: ReconciliationIdentity
  /** The GL balance (or movement) of the role. */
  ledger: string
  /** The same figure from Projects' tables. */
  projects: string
  difference: string
  /** Postings to the role from other sources (e.g. manual journals to contract revenue). */
  other: string
  /** Per contract (project), for drill-down. */
  byProject: {
    projectId: string
    code: string
    name: string
    ledger: string
    projects: string
    difference: string
  }[]
}
export interface ContractingReconciliation {
  asOf: string
  lines: ReconciliationLine[]
}

/** The GL roles a Sales document may carry (04 §9.3); set by the server from the link kind. */
export const SALES_GL_ROLES = ['contract_revenue', 'customer_advances', 'retention_receivable'] as const
export type SalesGlRole = (typeof SALES_GL_ROLES)[number]

/** Recovery method labels the UI shows (contract terms). */
export const RECOVERY_METHODS: readonly RecoveryMethod[] = ['PRO_RATA', 'FIXED_RATE', 'MANUAL']

// ── internal approval (P5) ───────────────────────────────────────────────────

/**
 * With `variationsApproval` / `claimsApproval` = WORKFLOW, "Submit" starts an approval run and the record
 * waits in PENDING_APPROVAL; the run's final approval performs the submit (numbering, freezing, notice);
 * a return or rejection sends it back to DRAFT. `POST …/approve-internal` / `…/return-internal` serve the
 * four-eyes path when no workflow applies.
 */
export const internalDecisionSchema = z.object({ version: versionSchema, comment: optionalText(1000) })
export const internalReturnSchema = z.object({ version: versionSchema, reason })

/**
 * The internal approval of a variation or claim, for the viewer (P5): the company's mode, and while it
 * waits in PENDING_APPROVAL the path (WORKFLOW: a run decides it from My approvals; SECOND_PERSON: a
 * projects.contract_approve holder other than its makers approves or returns it here) and what the viewer
 * may do. `blocked` says why approving is not offered.
 */
export interface InternalApproval {
  mode: 'NONE' | 'WORKFLOW'
  path: 'WORKFLOW' | 'SECOND_PERSON' | null
  /** The run deciding it (WORKFLOW path), for the link to the approval request. */
  runId: string | null
  can: { approve: boolean; return: boolean }
  blocked: 'SELF_APPROVAL' | 'NOT_PERMITTED' | 'WORKFLOW' | null
  /**
   * The latest return to DRAFT (four eyes or a run's return or rejection), while the record is in DRAFT again;
   * null once it is sent for approval again. Optional for older fixtures.
   */
  lastReturn?: { reason: string; by: ProjectUserRef | null; at: string } | null
}

// ── guarantees (P5) ──────────────────────────────────────────────────────────

export const GUARANTEE_DIRECTIONS = ['GIVEN', 'RECEIVED'] as const
export type GuaranteeDirection = (typeof GUARANTEE_DIRECTIONS)[number]
export const GUARANTEE_TYPES = ['BID', 'PERFORMANCE', 'ADVANCE_PAYMENT', 'RETENTION', 'MAINTENANCE'] as const
export type GuaranteeType = (typeof GUARANTEE_TYPES)[number]
/** EXPIRED is set by the daily job after the expiry date; extensions and reductions are events. */
export const GUARANTEE_STATUSES = ['ACTIVE', 'RELEASED', 'CALLED', 'EXPIRED'] as const
export type GuaranteeStatus = (typeof GUARANTEE_STATUSES)[number]
export const GUARANTEE_EVENT_KINDS = ['EXTEND', 'REDUCE', 'AMEND', 'RELEASE', 'CALL'] as const
export type GuaranteeEventKind = (typeof GUARANTEE_EVENT_KINDS)[number]

const guaranteeFields = z.object({
  direction: z.enum(GUARANTEE_DIRECTIONS),
  type: z.enum(GUARANTEE_TYPES),
  /** The bank's guarantee or letter number. */
  reference: requiredText(80),
  bankName: requiredText(200),
  /** GIVEN: in favour of the client (beneficiary); RECEIVED: from a supplier or subcontractor. */
  counterpartyName: requiredText(200),
  amount: moneySchema.refine((v) => Number(v) > 0, 'Enter an amount above zero'),
  issueDate: z.iso.date(),
  expiryDate: z.iso.date(),
  /** The last day a claim may be made, when it differs from the expiry. */
  claimEndDate: nullableDate,
  /** Cash margin the bank holds (information only; Finance moves the money). */
  marginAmount: nonNegativeMoney.nullish().transform((v) => v ?? null),
  notes: optionalText(2000),
})
const expiryAfterIssue = (v: { issueDate: string; expiryDate: string }) => v.expiryDate >= v.issueDate
const expiryIssue = { path: ['expiryDate'], message: 'The expiry is before the issue date' }
/** `POST /projects/:id/guarantees` (projects.contract; `bonds` function). */
export const guaranteeInputSchema = guaranteeFields.refine(expiryAfterIssue, expiryIssue)
export type GuaranteeInput = z.input<typeof guaranteeInputSchema>
export const guaranteeUpdateSchema = guaranteeFields
  .extend({ version: versionSchema })
  .refine(expiryAfterIssue, expiryIssue)
/**
 * `POST /projects/:id/guarantees/:gid/events`: EXTEND (new expiry), REDUCE (new amount), AMEND (either),
 * RELEASE (returned to the issuer), CALL (the beneficiary claimed it; amount called). Evidence is attached
 * to the guarantee through files.
 */
export const guaranteeEventSchema = z.object({
  version: versionSchema,
  kind: z.enum(GUARANTEE_EVENT_KINDS),
  date: z.iso.date(),
  newAmount: nonNegativeMoney.nullish().transform((v) => v ?? null),
  newExpiryDate: nullableDate,
  note: requiredText(1000, 'Say what happened'),
})
export type GuaranteeEventInput = z.input<typeof guaranteeEventSchema>

export interface GuaranteeEvent {
  id: string
  kind: GuaranteeEventKind
  date: string
  newAmount: string | null
  newExpiryDate: string | null
  note: string
  by: ProjectUserRef | null
  at: string
}

export interface ProjectGuarantee {
  id: string
  project: { id: string; code: string; name: string; nameAr: string | null }
  direction: GuaranteeDirection
  type: GuaranteeType
  reference: string
  bankName: string
  counterpartyName: string
  amount: string
  /** amount ÷ the contract's current value × 100, one decimal; null without a contract. */
  percentOfContract: string | null
  issueDate: string
  expiryDate: string
  claimEndDate: string | null
  marginAmount: string | null
  status: GuaranteeStatus
  /** Days to expiry (negative once past); null once released or called. */
  daysToExpiry: number | null
  notes: string | null
  events: GuaranteeEvent[]
  can: { edit: boolean; event: boolean }
  version: number
}

/** `GET /projects/guarantees` (tenant register) and the contingent-liabilities report as at a date. */
export const guaranteeListQuerySchema = z.object({
  projectId: z.uuid().optional(),
  direction: z.enum(GUARANTEE_DIRECTIONS).optional(),
  type: z.enum(GUARANTEE_TYPES).optional(),
  status: z.enum(GUARANTEE_STATUSES).optional(),
  /** Expiring within this many days. */
  expiringWithin: z.coerce.number().int().min(0).max(365).optional(),
  asOf: z.iso.date().optional(),
  /** Text in the reference (bond number), the bank or the counterparty (beneficiary or issuer). */
  q: z.string().trim().max(200).optional(),
  format: z.enum(['json', 'csv']).default('json'),
})
export type GuaranteeListQuery = z.input<typeof guaranteeListQuerySchema>
export interface ContingentLiabilities {
  asOf: string
  /** GIVEN guarantees in force at `asOf` (amounts as at that date, from the events). */
  given: { type: GuaranteeType; count: number; amount: string }[]
  received: { type: GuaranteeType; count: number; amount: string }[]
  items: ProjectGuarantee[]
}

// ── forfeit, advance reduction, final account (P5) ───────────────────────────

/**
 * `POST /projects/:id/retention/forfeit` (projects.bill): retention the contractor gives up (the client
 * keeps it for defects). Projects journal Dr contract revenue / Cr retention receivable (source type
 * `ProjectRetentionForfeit`); at most held − released − forfeited (FORFEIT_EXCEEDS_HELD).
 */
export const retentionForfeitSchema = z.object({
  requestId: z.uuid(),
  amount: moneySchema.refine((v) => Number(v) > 0, 'Enter an amount above zero'),
  date: z.iso.date(),
  reason,
})
export type RetentionForfeitInput = z.input<typeof retentionForfeitSchema>

/**
 * `POST /projects/:id/advances/reduce` (projects.bill): reduces the advance by a Projects-issued
 * allowance credit note on the advance invoice (GL role customer_advances, link kind ADVANCE_REDUCTION,
 * VAT at the advance's rate), at most the outstanding advance (REDUCTION_EXCEEDS_OUTSTANDING). It also
 * lets a FINAL certificate pass when the rest of the advance exceeds its gross increment (P4 decision 21).
 */
export const advanceReductionSchema = z.object({
  requestId: z.uuid(),
  amount: moneySchema.refine((v) => Number(v) > 0, 'Enter an amount above zero'),
  date: z.iso.date(),
  reason,
})
export type AdvanceReductionInput = z.input<typeof advanceReductionSchema>
/** `POST /projects/:id/advances/reductions/:documentId/void` (projects.bill): under Sales' refusals. */
export const advanceReductionVoidSchema = z.object({ reason })
/** `POST /projects/:id/retention/forfeits/:forfeitId/void` (projects.bill): reverses its journal. */
export const retentionForfeitVoidSchema = z.object({ reason })

export interface RetentionForfeit {
  id: string
  amount: string
  date: string
  reason: string
  status: 'ACTIVE' | 'VOID'
  voidReason: string | null
  journalEntryId: string | null
  canVoid: boolean
}

export interface AdvanceReduction {
  /** The credit note (link kind ADVANCE_REDUCTION). */
  document: BillingDocumentRef | null
  /** Net of VAT. */
  amount: string
  date: string
  reason: string | null
  canVoid: boolean
}

/**
 * `GET /projects/:id/contract/statement?lang=` (read): the final account (04 §15.3). `POST
 * /contract/close {version}` (projects.contract): CLOSED, refused while a claim is open or any identity of
 * the project is not zero (CONTRACT_NOT_SETTLED with the figures).
 */
export interface ContractStatement {
  asOf: string
  originalSum: string
  variations: { number: string; title: string; approvedValue: string; decidedOn: string | null }[]
  currentValue: string
  finalCertifiedGross: string
  delayDamages: string
  retention: { held: string; released: string; forfeited: string; balance: string }
  advance: { invoiced: string; recovered: string; reduced: string; outstanding: string }
  documents: {
    kind: 'CLAIM' | 'ADVANCE' | 'RETENTION_RELEASE' | 'ADVANCE_REDUCTION'
    number: string
    date: string
    total: string
    received: string
    balance: string
  }[]
  /** What the client still owes (positive) or is owed back (negative). */
  balanceDue: string
  /** The identities of 04 §12 for this contract; all zero before Close. */
  checks: { identity: ReconciliationIdentity; difference: string }[]
  closable: boolean
  /** The claim that stops Close (not yet certified or rejected), if any. Optional for older fixtures. */
  openClaim?: {
    id: string
    number: string | null
    kind: ClaimKind
    status: ClaimStatus
    periodTo: string
  } | null
}
export const contractCloseSchema = z.object({ version: versionSchema })

// ── money-free progress (P5) ─────────────────────────────────────────────────

/**
 * `GET /projects/:id/contract/progress` (projects.read in scope): the contract without money, for
 * managers and site staff who don't hold projects.contract or projects.financials (06 §1 rule 7).
 */
export interface ContractProgress {
  status: ContractStatus
  commencementDate: string | null
  completionDue: string | null
  practicalCompletion: string | null
  dlpEnd: string | null
  variations: {
    number: string
    title: string
    status: VariationStatus
    timeImpactDays: number
    decidedOn: string | null
  }[]
  boq: {
    key: string
    itemNo: string
    description: string
    descriptionAr: string | null
    unit: string
    quantity: string
    certifiedPercent: string | null
    sectionPath?: { id: string; code: string; title: string; titleAr: string | null }[]
  }[]
  claims: { number: string | null; status: ClaimStatus; periodTo: string; certifiedOn: string | null }[]
}

// ── errors ───────────────────────────────────────────────────────────────────

export const CONTRACTING_ERROR_CODES = [
  'FEATURE_REQUIRES',
  'CONTRACT_ACTIVE',
  'CONTRACT_NOT_ACTIVE',
  'BOQ_REQUIRED',
  'CUSTOMER_REQUIRED',
  'BOQ_FROZEN',
  'IMPORT_STALE',
  'VARIATION_FROZEN',
  'BELOW_CERTIFIED',
  'CLAIM_OPEN',
  'FINAL_CERTIFIED',
  'CLAIM_NOT_SUBMITTED',
  /** 422: only a DRAFT claim is edited as applied or deleted. */
  'CLAIM_NOT_DRAFT',
  /** 422: only a CERTIFIED claim is voided. */
  'CLAIM_NOT_CERTIFIED',
  'OVER_MEASURE',
  'PERCENT_OVER_100',
  'OVERRIDE_REASON_REQUIRED',
  'NEGATIVE_CERTIFICATE',
  'RETENTION_BELOW_RELEASED',
  /** 422: an override puts recovery to date above the advance invoiced. */
  'RECOVERY_ABOVE_ADVANCE',
  /** 422: an override puts retention to date above the gross to date. */
  'RETENTION_ABOVE_GROSS',
  /** 422: a certificate or release dated in the future, or before the latest certificate. */
  'CERTIFICATE_DATE_ORDER',
  'CLAIM_NOT_LATEST',
  'CLAIM_HAS_LATER',
  'RETENTION_RELEASED',
  'ADVANCE_EXISTS',
  'ADVANCE_RECOVERED',
  'RETENTION_EXCEEDS_HELD',
  'BILLING_BY_CLAIMS',
  'GL_ROLE_LINES',
  /** 422 (P5): closing a contract with an open claim or a non-zero identity. */
  'CONTRACT_NOT_SETTLED',
  /** 422 (P5): a forfeit above what is held, or a reduction above the outstanding advance. */
  'FORFEIT_EXCEEDS_HELD',
  'REDUCTION_EXCEEDS_OUTSTANDING',
  /** 422 (P5): a RELEASED or CALLED guarantee is final; it takes no edit or event. */
  'GUARANTEE_CLOSED',
  /** 422 (P5): a guarantee event dated before its latest event or its issue date, or after today. */
  'GUARANTEE_EVENT_ORDER',
  /** 422 (P5): acknowledging a signal that doesn't hold now. */
  'SIGNAL_NOT_ACTIVE',
  'CONTROL_ACCOUNT',
  'ROLE_ACCOUNT_SHARED',
] as const
export type ContractingErrorCode = (typeof CONTRACTING_ERROR_CODES)[number]
